import { LocaleService } from "../../shared/i18n/locale.service";
import { AuthService } from '@auth0/auth0-angular';
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { ChatService } from '../../shared/services/chat.service';
import { EventService } from '../../shared/services/event.service';
import { PlayerService } from '../../shared/services/player.service';
import { PlayerEventInfoService } from '../../shared/services/playereventinfo.service';
import { DelayedSpinnerService } from '../../shared/services/delayed-spinner.service';
import { DialogService } from '../../core/dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';

// Resolve before activation: the source screen stays visible until access and the roster are ready.
export const matchScreenResolver: ResolveFn<unknown> = route => {
  const locale = inject(LocaleService);
  const events = inject(EventService);
  const chats = inject(ChatService);
  const players = inject(PlayerService);
  const roster = inject(PlayerEventInfoService);
  const spinner = inject(DelayedSpinnerService);
  const router = inject(Router);
  const dialogs = inject(DialogService);
  const translate = inject(TranslateService);
  let saved;
  try { saved = JSON.parse(localStorage.getItem('event') || 'null'); } catch { saved = null; }
  const eventId = route.queryParamMap.get('eventId') || route.params?.['eventId'] || saved?.id;
  if (!eventId) return of(null);
  let playerId = players.getCurrentPlayer()?.id;
  let homeAction;
  try { homeAction = JSON.parse(sessionStorage.getItem('matchHomeAction') || 'null'); } catch { homeAction = null; }
  const finishPreparation = spinner.acquire();
  return players.getCurrentUserProfile().pipe(
    switchMap(player => { playerId = player.id; return locale.initialize(player); }),
    switchMap(() => events.getRichEvent(eventId)),
    switchMap(event => {
      const manager = event.manager?.id === players.getCurrentPlayer()?.id;
      const member = manager || event.access?.member || event.viewedByPlayer;
      if (!member && route.routeConfig?.path?.startsWith('event-players')) {
        router.navigate(['/event-detail', false], {queryParams: {eventId}});
        return EMPTY;
      }
      return forkJoin({
        blocked: manager && homeAction?.eventId === eventId && homeAction?.action === 'players' ? events.getBlockedPlayers(eventId) : of(null),
        chat: member && event.chat?.id ? chats.getOptionalMatchChat(event.chat.id) : of(null),

        participants: of(event.playerEventInfos || [])
      }).pipe(map(({ participants, chat, blocked }) => {
        const ownParticipation = participants.find(item => item.player?.id === playerId);
        const candidate = !!event.access?.requestPending || event.viewedByCandidate === true ||
          event.participation?.teamMemberType === 'CANDIDATE' || ownParticipation?.teamMemberType === 'CANDIDATE';
        const prepared = { ...event, viewedByManager: manager, viewedByPlayer: !!member && !candidate,
          viewedByCandidate: candidate, currentPlayerInviteStatus: ownParticipation?.status || event.participation?.status, playerEventInfos: participants };
        if (players.getCurrentPlayer()?.id !== playerId) throw new Error('Session changed');
        localStorage.setItem('event', JSON.stringify(prepared));
        return {event: prepared, chat, blocked};
      }));
    }),
    catchError(error => {
      dialogs.alert({ title: translate.instant('common.alert'), text: translate.instant([403, 404].includes(error?.status) ? 'events.openMatchPreviewMissingTitle' : 'errors.generic') });
      if (!router.navigated) router.navigate(['/event-list']);
      return EMPTY;
    }),
    finalize(finishPreparation)
  );
};

// The home route also prepares its complete card payload before activation.
export const matchHomeResolver: ResolveFn<unknown> = () => {
  const auth = inject(AuthService);
  const locale = inject(LocaleService);
  const events = inject(EventService);
  const players = inject(PlayerService);
  const chats = inject(ChatService);
  const spinner = inject(DelayedSpinnerService);
  return auth.isAuthenticated$.pipe(take(1), switchMap(authenticated => {
    if (!authenticated) return of(null);
    const finishPreparation = spinner.acquire();
    return players.getCurrentUserProfile().pipe(switchMap(player => {
      const coordinates = player.location?.coordinates;
      const latitude = coordinates?.[0], longitude = coordinates?.[1];
      const locationAvailable = Number.isFinite(latitude) && Number.isFinite(longitude) && !!player.city;
      return forkJoin({
        myEvents: events.getMyHomeEvents(),
        nearbyEvents: locationAvailable ? events.getNearbyHomeEvents(latitude, longitude) : of([]),
        chats: chats.getChatsForPlayer(player.id)
      }).pipe(map(data => ({...data, player, locationAvailable})));
    }), catchError(() => of({error: true})), finalize(finishPreparation));
  }));
};
